// @ts-strict-ignore
import { IBaseArticleProjectProcessor } from './i-base-article-project.processor';
import { ProjectModel } from '@project/model/project.model';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ArticleAccessoryManager } from '../manager/article-accessory.manager';
import { ArticleUsageVO, ArticleVO } from './vo/article.vo';
import { ConnectionProjectData } from './connection-project-data';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUtils } from '../util/article-utils';
import { ArticleTypeConstants } from '../constants/article-type-constants';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleProjectData } from './article-project-data';
import { RangeOnLineArticleData } from '@project/components/article/model/range-on-line-article-data';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { InsertArticleHelper } from '../helper/insert-article.helper';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { OnRailReferenceArticleLocationVO } from '@project/components/article/model/vo/on-rail-reference-article-location.vo';
import { CustomArticleVO } from '@project/components/article/model/vo/custom-article.vo';
import { RelativePositionReferenceArticleLocationVO } from '@project/components/article/model/vo/relative-position-reference-article-location.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { SelectedConnectionsReferenceArticleLocationVO } from './vo/selected-connections-reference-article-location.vo';
import { SelectedArticlesReferenceArticleLocationVO } from './vo/selected-articles-reference-article-location.vo';
import { ArticleLocationVO } from './vo/article-location.vo';
import { CustomArticleUtils } from '../util/custom-article-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { InsertedArticleData } from '@project/components/article/processor/i-article-location.processor';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArticleService } from '../service/article.service';
import { AddArticleError } from '@project/components/article/processor/add-article-error';

export class BaseMountingRailArticleProjectProcessor implements IBaseArticleProjectProcessor {
  constructor(
    private _notificationMessage: string,
    protected projectModel: ProjectModel,
    protected projectModelService: ProjectModelService,
    protected projectArticleService: ProjectArticleService,
    private mountingRailHelper: MountingRailHelper,
    private selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    private articleAccessoryManager: ArticleAccessoryManager,
    private localeResourceManager: LocaleResourceManager,
    protected connectionInfoUtils: ConnectionInfoUtils,
    private labelingUtils: LabelingUtils,
    protected alertHelper: AlertHelper,
    private _insertArticleService: InsertArticleService,
    private articleService: ArticleService
  ) {}

  public prepareProjectDataBySelection(articleToAdd: ArticleVO): any[] {
    if (this.selectionModel.isConnectionSelection()) {
      const connections: Connection[] = this.labelingUtils.collectAllMergedTextureConnections(this.selectionModelService.getConnections());
      return this.prepareProjectDataByConnectionSelection(connections, articleToAdd);
    } else {
      return this.prepareProjectDataByArticleSelection(this.selectionModelService.getArticleContainers(), articleToAdd);
    }
  }

  protected prepareProjectDataByConnectionSelection(selectedConnectionCubes: Connection[], articleToAdd: ArticleVO): any[] {
    const connectionProjectData: ConnectionProjectData = new ConnectionProjectData();
    connectionProjectData.connectionCubes = selectedConnectionCubes;
    return [connectionProjectData];
  }

  protected prepareProjectDataByArticleSelection(selectedArticleContainers: ArticleContainer[], articleToAdd: ArticleVO): any[] {
    let connectArticleToSelectedArticles: boolean = false;
    if (
      articleToAdd &&
      NumberUtils.isNumber(articleToAdd.identity) &&
      !ArticleUtils.isEndPlateArticle(articleToAdd) &&
      !ArticleUtils.isArticleOfType(articleToAdd, ArticleTypeConstants.TYPE_END_STOP)
    ) {
      if (selectedArticleContainers && selectedArticleContainers.length > 0) {
        selectedArticleContainersLoop: for (const selectedArticleContainer of selectedArticleContainers) {
          if (!selectedArticleContainer.article || this.articleService.isRangeOnLineArticle(selectedArticleContainer.article)) {
            continue selectedArticleContainersLoop;
          }
          const isArticleAccessoryOfSelectedArticles: boolean = this.articleAccessoryManager.isAccessoryArticleExists(
            selectedArticleContainer.article.identity,
            articleToAdd.identity
          );
          if (isArticleAccessoryOfSelectedArticles) {
            connectArticleToSelectedArticles = true;
            break selectedArticleContainersLoop;
          }
        }
      }
    }
    if (connectArticleToSelectedArticles) {
      // prepare data to connect given article as accessory to selected articles
      const articleProjectData: ArticleProjectData = new ArticleProjectData();
      articleProjectData.articleContainers = selectedArticleContainers;
      return [articleProjectData];
    } else {
      // otherwise try prepare data to connect given article to the rail (range-on-line connection)
      return this.createRangeOnLineArticleDataByArticles(selectedArticleContainers);
    }
  }

  protected createRangeOnLineArticleDataByArticles(articleContainers: ArticleContainer[]): RangeOnLineArticleData[] {
    return this.mountingRailHelper.createRangeOnLineArticleDataByArticles(articleContainers, null, false);
  }

  public async addArticleByProjectData(projectData: any, articleToAdd: ArticleVO): Promise<InsertedArticleData> {
    if (projectData instanceof ConnectionProjectData) {
      return this.insertArticleBySelectedConnections(projectData.connectionCubes, articleToAdd);
    } else if (projectData instanceof RangeOnLineArticleData) {
      return this.addArticleByRangeOnLineData(projectData as RangeOnLineArticleData, articleToAdd);
    } else if (projectData instanceof ArticleProjectData) {
      return this.insertArticleBySelectedArticles(projectData.articleContainers, articleToAdd);
    }
  }

  public async addArticleByNeighbor(neighborUsageId: string, articleToAdd: ArticleVO): Promise<InsertedArticleData> {
    return this.insertArticleByNeighbor(articleToAdd, this.projectModel.getArticleUsage(neighborUsageId));
  }

  private async addArticleByRangeOnLineData(rangeOnLineData: RangeOnLineArticleData, articleToAdd: ArticleVO): Promise<InsertedArticleData> {
    if (rangeOnLineData.selectedArticlesOnRail.length > 0) {
      return this.insertArticleByNeighbor(articleToAdd, rangeOnLineData.lastSelectedArticle.articleUsage);
    } else if (rangeOnLineData.baseArticleSelected) {
      return this.insertArticleOnRail(articleToAdd, rangeOnLineData);
    } else {
      const message: string = this.localeResourceManager
        .getValueByKey('addArticleToRelativeLocationProcessor.message.range_on_line_connection_not_found');
      throw new AddArticleError(message);
    }
  }

  public isReplaceOperationAvailable(): boolean {
    const selectedArticlesOnRail: ArticleContainer[] = this.mountingRailHelper.getSelectedArticleContainersOnRail();
    return selectedArticlesOnRail.length > 0;
  }

  public isMultipleInsertAvailable(): boolean {
    return true;
  }

  private async insertArticleOnRail(article: ArticleVO, rangeOnLineData: RangeOnLineArticleData): Promise<InsertedArticleData> {
    const insertHelper: InsertArticleHelper = rangeOnLineData.insertHelper;
    if (!insertHelper) {
      return
    }
      const connectedArticles: ArticleContainer[] = insertHelper.getAllConnectedArticles(true);
      let articleOffset: number = insertHelper.getConfiguredStartOffset();
      if (connectedArticles.length > 0) {
        const lastConnectedArticleLine: AdvancedLine = insertHelper.getArticleLine(connectedArticles[connectedArticles.length - 1]);
        articleOffset += insertHelper.getDistanceFromConfiguredStart(lastConnectedArticleLine.end);
      }

      const articleLocation: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
      articleLocation.rangeOnLineArticleUsage = this.projectModel.getArticleUsage(rangeOnLineData.baseUsageId);
      articleLocation.articleOffset = articleOffset;
      if (article instanceof CustomArticleVO) {
        this.processArticleLocationByCustomArticle(article as CustomArticleVO, articleLocation);
      }
      return this._insertArticleService.insertArticleByArticleLocation(article, articleLocation, this._notificationMessage);
  }

  public async insertArticleLeftOfSelectedArticles(article: ArticleVO, rangeOnLineData: RangeOnLineArticleData): Promise<InsertedArticleData> {
    const insertHelper: InsertArticleHelper = rangeOnLineData.insertHelper;
    if (insertHelper) {
      let articleOffset: number = insertHelper.getConfiguredStartOffset();

      const firstSelectedArticle: ArticleContainer = rangeOnLineData.firstSelectedArticle;
      if (firstSelectedArticle) {
        const firstSelectedArticleLine: AdvancedLine = insertHelper.getArticleLine(firstSelectedArticle);
        articleOffset += insertHelper.getDistanceFromConfiguredStart(firstSelectedArticleLine.start);
      }

      const articleLocation: OnRailReferenceArticleLocationVO = new OnRailReferenceArticleLocationVO();
      articleLocation.rangeOnLineArticleUsage = this.projectModel.getArticleUsage(rangeOnLineData.baseUsageId);
      articleLocation.articleOffset = articleOffset;
      if (article instanceof CustomArticleVO) {
        this.processArticleLocationByCustomArticle(article as CustomArticleVO, articleLocation);
      }
     return this._insertArticleService.insertArticleByArticleLocation(article, articleLocation, this._notificationMessage);
    }
  }

  private async insertArticleByNeighbor(article: ArticleVO, neighborArticleUsage: ArticleUsageVO): Promise<InsertedArticleData> {
    if (!neighborArticleUsage) {
      return;
    }
    const neighborArticleLocation: RelativePositionReferenceArticleLocationVO = new RelativePositionReferenceArticleLocationVO();
    neighborArticleLocation.referenceArticleUsage = neighborArticleUsage;
    neighborArticleLocation.relativePosition = RelativePositionReferenceArticleLocationVO.RELATIVE_POSITION_RIGHT_OF;
    if (article instanceof CustomArticleVO) {
      this.processArticleLocationByCustomArticle(article, neighborArticleLocation);
    }
    return this._insertArticleService.insertArticleByArticleLocation(article, neighborArticleLocation, this._notificationMessage);
  }

  private async insertArticleBySelectedConnections(connectionCubes: Connection[], article: ArticleVO): Promise<InsertedArticleData> {
    if (!connectionCubes || connectionCubes.length === 0) {
      return;
    }
    const selectedConnectionLocation: SelectedConnectionsReferenceArticleLocationVO = new SelectedConnectionsReferenceArticleLocationVO();
    selectedConnectionLocation.acceptingCubes = connectionCubes;
    return this._insertArticleService.insertArticleByArticleLocation(article, selectedConnectionLocation, this._notificationMessage);
  }

  private async insertArticleBySelectedArticles(articleContainers: ArticleContainer[], article: ArticleVO): Promise<InsertedArticleData> {
    if (!articleContainers || articleContainers.length === 0) {
      return;
    }
    const selectedArticlesReferenceArticleLocation: SelectedArticlesReferenceArticleLocationVO = new SelectedArticlesReferenceArticleLocationVO();
    selectedArticlesReferenceArticleLocation.articleContainers = articleContainers;
    return this._insertArticleService.insertArticleByArticleLocation(article, selectedArticlesReferenceArticleLocation, this._notificationMessage);
  }

  private processArticleLocationByCustomArticle(customArticle: CustomArticleVO, articleLocation: ArticleLocationVO): void {
    const articleUsageToCreate: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(customArticle.identity, customArticle.version);
    CustomArticleUtils.applyCustomArticleInfoToArticleUsage(articleUsageToCreate, customArticle.customArticleInfo);
    articleLocation.existingArticleUsageId = articleUsageToCreate.id;
    articleLocation.checkConnectionProperties = false;
  }

  public getArticleUsageIdsToDetach(): string[] {
    return [];
  }
}
